import { ActionReducerMapBuilder, nanoid } from '@reduxjs/toolkit';
import { ChatRole } from 'modules/chat/domain/enums/chat-role';
import { isChatForbiddenError, readChatRequestError } from 'modules/chat/domain/interfaces';
import { clearChatHistory, loadChatHistory, sendChatMessage } from './actions';
import { ChatState } from './chat-store';

type ReducerBuilder = ActionReducerMapBuilder<ChatState>;

function appendAssistantMessage(state: ChatState, content: string) {
  state.messages.push({
    id: nanoid(),
    role: ChatRole.ASSISTANT,
    content,
    createdAt: Date.now(),
  });
}

function applyChatFailure(state: ChatState, payload: unknown, fallback: string, appendForbidden: boolean) {
  const chatError = readChatRequestError(payload, fallback);

  if (isChatForbiddenError(chatError)) {
    if (appendForbidden) {
      appendAssistantMessage(state, chatError.message);
    }
    return;
  }

  state.error = chatError.message;
}

export function createSendChatMessageReducer(builder: ReducerBuilder) {
  builder.addCase(sendChatMessage.pending, (state, action) => {
    state.messages.push({
      id: nanoid(),
      role: ChatRole.USER,
      content: action.meta.arg,
      createdAt: Date.now(),
    });
    state.isSending = true;
    state.error = undefined;
  });

  builder.addCase(sendChatMessage.fulfilled, (state, action) => {
    state.messages = state.messages.map((message) =>
      message.pendingConfirmation ? { ...message, pendingConfirmation: null } : message,
    );
    state.messages.push({
      id: nanoid(),
      role: ChatRole.ASSISTANT,
      content: action.payload.answer,
      createdAt: Date.now(),
      pendingConfirmation: action.payload.pendingConfirmation,
    });
    state.quota = action.payload.quota ?? undefined;
    state.pendingConfirmation = action.payload.pendingConfirmation;
    state.isSending = false;
  });

  builder.addCase(sendChatMessage.rejected, (state, action) => {
    state.isSending = false;
    applyChatFailure(state, action.payload, 'Не удалось отправить сообщение', true);
  });
}

export function createLoadChatHistoryReducer(builder: ReducerBuilder) {
  builder.addCase(loadChatHistory.pending, (state) => {
    state.isLoadingHistory = true;
    state.error = undefined;
  });

  builder.addCase(loadChatHistory.fulfilled, (state, action) => {
    if (!state.isSending) {
      state.messages = action.payload;
    }
    state.isLoadingHistory = false;
  });

  builder.addCase(loadChatHistory.rejected, (state, action) => {
    state.isLoadingHistory = false;
    applyChatFailure(state, action.payload, 'Не удалось загрузить историю', !state.isSending);
  });
}

export function createClearChatHistoryReducer(builder: ReducerBuilder) {
  builder.addCase(clearChatHistory.pending, (state) => {
    state.isClearing = true;
    state.error = undefined;
  });

  builder.addCase(clearChatHistory.fulfilled, (state) => {
    state.messages = [];
    state.pendingConfirmation = null;
    state.isClearing = false;
  });

  builder.addCase(clearChatHistory.rejected, (state, action) => {
    state.isClearing = false;
    applyChatFailure(state, action.payload, 'Не удалось очистить историю', true);
  });
}
